import CheckIcon from '@mui/icons-material/Check'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ButtonBase from '@mui/material/ButtonBase'
import Divider from '@mui/material/Divider'
import Fade from '@mui/material/Fade'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { HTMLAttributes, MouseEvent, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'
import { TextV2 } from 'shared/components/Text/TextV2'
import { UserAvatar } from 'shared/components/UserAvatar/UserAvatar'
import { Handler } from 'shared/helpers/typeHelper'
import { Colors } from 'shared/theme/colors'
import { TYPOGRAPHY } from 'shared/theme/typographyV2'
import styled from 'styled-components'

import { useAnalyticsEvent } from '../../../hooks/useAnalyticsEvent'
import { currentUserSelector } from '../../../redux/selectors/users'
import { PROFILE_PATH } from '../../../Routes'
import { OrganizationSwitchDialog, useOrganizationMemberships } from './OrganizationSwitcher'

export enum AvatarMenuOptions {
  PROFILE = 'PROFILE',
  LOGOUT = 'LOGOUT',
}

interface AvatarMenuProps {
  handleLogout: Handler
  options?: AvatarMenuOptions[]
}

/** Static rows inside the menu. MUI skips them when choosing the item to focus. */
function MenuText(props: HTMLAttributes<HTMLLIElement>) {
  return <li role="presentation" {...props} />
}
MenuText.muiSkipListHighlight = true

export const AvatarMenu = ({ handleLogout, options: optionsProp }: AvatarMenuProps) => {
  const track = useAnalyticsEvent()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selection, setSelection] = useState('')
  const navigate = useNavigate()
  const currentUser = useSelector(currentUserSelector)
  const memberships = useOrganizationMemberships(currentUser.id)
  const options = optionsProp || [AvatarMenuOptions.PROFILE, AvatarMenuOptions.LOGOUT]
  const list = memberships.data?.memberships || []
  const current = memberships.data?.current
  const fullName = [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ')
  const open = Boolean(anchorEl)

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    track('nav_avatar_click')
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleProfileClick = () => {
    navigate(
      currentUser.is_staff
        ? `/manage/users/${currentUser.id}/show`
        : currentUser.can_operate_fleet && !currentUser.is_team_driver
          ? `/manage/users/${currentUser.id}/`
          : PROFILE_PATH
    )
    handleClose()
  }

  const chooseOrganization = (id: string) => {
    handleClose()
    if (id !== current) setSelection(id)
  }

  // Accounts without memberships (vendor sign-ins, for example) get no organization section
  // rather than a loading state that never finishes.
  const showOrganizations = memberships.isLoading || memberships.isError || list.length > 0
  const organizationSection = showOrganizations
    ? [
        <MenuText key="organization-title" className="px-4 pt-3 pb-1">
          <TextV2 type={TYPOGRAPHY.uppercaseLabel3} className="text-mid-gray">
            Organization
          </TextV2>
        </MenuText>,
        memberships.isError ? (
          <MenuItem key="organization-retry" onClick={() => memberships.refetch()}>
            Retry organizations
          </MenuItem>
        ) : memberships.isLoading ? (
          <MenuText key="organization-loading" className="px-4 pb-2">
            <TextV2 type={TYPOGRAPHY.paragraph2} className="text-mid-gray">
              Loading organization…
            </TextV2>
          </MenuText>
        ) : list.length < 2 ? (
          <MenuText key="organization-current" className="flex items-center gap-3 px-4 pb-2">
            <TextV2 type={TYPOGRAPHY.paragraph2} className="min-w-0 flex-1 truncate">
              {list[0].name}
            </TextV2>
            <CheckIcon fontSize="small" className="text-royal-blue" titleAccess="Current organization" />
          </MenuText>
        ) : (
          list.map((membership) => {
            const active = membership.id === current
            return (
              <MenuItem
                key={membership.id}
                role="menuitemradio"
                aria-checked={active}
                selected={active}
                onClick={() => chooseOrganization(membership.id)}
              >
                <span className="min-w-0 flex-1 truncate">{membership.name}</span>
                {active && <CheckIcon fontSize="small" className="text-royal-blue ml-3" />}
              </MenuItem>
            )
          })
        ),
        memberships.data?.requires_sign_in ? (
          <MenuText key="organization-sign-in" className="px-4 pt-1 pb-2">
            <TextV2 type={TYPOGRAPHY.paragraph3} className="text-mid-gray">
              Sign in again with a code to access your other organizations.
            </TextV2>
          </MenuText>
        ) : null,
      ]
    : null

  return (
    <>
      <AccountButton
        onClick={handleClick}
        aria-label="Your account"
        aria-haspopup="menu"
        aria-expanded={open}
        focusRipple
      >
        <UserAvatar width="36px" height="36px" fontSize="12px" userName={fullName} />
        {currentUser.first_name && (
          <TextV2 type={TYPOGRAPHY.label3} className="xs:hidden max-w-32 truncate font-semibold" aria-hidden>
            {currentUser.first_name}
          </TextV2>
        )}
        <ExpandMoreIcon fontSize="small" className="xs:hidden text-mid-gray" aria-hidden />
      </AccountButton>
      <Menu
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 280, maxWidth: 360 } } }}
        data-testid="menu-options"
        TransitionComponent={Fade}
      >
        <MenuText className="px-4 pt-2 pb-3">
          <TextV2 type={TYPOGRAPHY.label2} className="truncate font-semibold">
            {fullName || 'Your account'}
          </TextV2>
          {currentUser.email && (
            <TextV2 type={TYPOGRAPHY.paragraph3} className="text-mid-gray truncate">
              {currentUser.email}
            </TextV2>
          )}
        </MenuText>
        <Divider />
        {organizationSection}
        {organizationSection && <Divider />}
        {options.includes(AvatarMenuOptions.PROFILE) && (
          <MenuItem onClick={handleProfileClick}>See my profile</MenuItem>
        )}
        {options.includes(AvatarMenuOptions.LOGOUT) && (
          <MenuItem onClick={handleLogout} data-testid="logout">
            Log Out
          </MenuItem>
        )}
      </Menu>
      <OrganizationSwitchDialog
        key={selection}
        membership={list.find((membership) => membership.id === selection) || null}
        currentUserId={currentUser.id}
        onClose={() => setSelection('')}
      />
    </>
  )
}

const AccountButton = styled(ButtonBase)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  padding: 4px 6px 4px 4px;
  border-radius: 999px;
  font-family: inherit;
  transition: background-color 150ms ease;

  &:hover {
    background-color: ${Colors.bgGray};
  }

  &:focus-visible {
    outline: 2px solid ${Colors.royalBlue};
    outline-offset: 2px;
  }
`
